'use client';

import type { CrawlerPeriodicSettingsDto } from '@terra/shared-contracts-applications';
import { DEFAULT_CRAWLER_PERIODIC_SETTINGS } from '@terra/shared-contracts-applications';
import { SettingsBooleanControl } from '../SettingsBooleanControl';
import { SettingsCard } from '../SettingsCard';
import { SettingsRow } from '../SettingsRow';
import {
  getBrowserTimezone,
  resolveScheduleTimezoneSelection,
} from '../../automation/automationScheduleShared';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { CrawlerWindowDetailsSection } from './CrawlerWindowDetailsSection';

export type CrawlerConfigurationCardProps = {
  crawlerPeriodic?: CrawlerPeriodicSettingsDto | null;
  disabled?: boolean;
  isUpdating?: boolean;
  onChange: (crawlerPeriodic: CrawlerPeriodicSettingsDto) => void;
};

const resolveSettings = (
  crawlerPeriodic: CrawlerPeriodicSettingsDto | null | undefined,
): CrawlerPeriodicSettingsDto => ({
  ...DEFAULT_CRAWLER_PERIODIC_SETTINGS,
  ...crawlerPeriodic,
  enabled: Boolean(crawlerPeriodic?.enabled),
  windowEnabled: Boolean(crawlerPeriodic?.windowEnabled),
});

export function CrawlerConfigurationCard({
  crawlerPeriodic,
  disabled,
  isUpdating,
  onChange,
}: CrawlerConfigurationCardProps) {
  const t = useTranslations('designSystem.automation.crawler');
  const tCommon = useTranslations('designSystem.common');
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const settings = resolveSettings(crawlerPeriodic);
  const isEnabled = settings.enabled;
  const isWindowEnabled = isEnabled && settings.windowEnabled;
  const controlsDisabled = Boolean(disabled || isUpdating);

  const selectedTimezone = resolveScheduleTimezoneSelection({
    scheduleTimezone: settings.timezone,
    browserTimezone,
  });

  const selectedDay =
    settings.dayOfWeek === null || settings.dayOfWeek === undefined
      ? 1
      : settings.dayOfWeek;
  const selectedStartHour =
    settings.startHour === null || settings.startHour === undefined
      ? 0
      : settings.startHour;

  const emit = (partial: Partial<CrawlerPeriodicSettingsDto>) => {
    onChange({
      ...settings,
      ...partial,
    });
  };

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      <SettingsCard>
        <VStack alignItems="stretch" gap={4} w="full">
          <SettingsRow
            label={t('biweekly.label')}
            tooltip={t('biweekly.tooltip')}
            description={t('biweekly.description')}
            control={
              <SettingsBooleanControl
                checked={isEnabled}
                label={isEnabled ? tCommon('yes') : tCommon('no')}
                disabled={controlsDisabled}
                onCheckedChange={(details) => {
                  if (!details.checked) {
                    emit({
                      enabled: false,
                      windowEnabled: false,
                      dayOfWeek: null,
                      startHour: null,
                      timezone: null,
                    });

                    return;
                  }

                  emit({
                    enabled: true,
                    windowEnabled: false,
                    dayOfWeek: null,
                    startHour: null,
                    timezone: null,
                  });
                }}
              />
            }
          />

          <SettingsRow
            label={t('window.label')}
            tooltip={t('window.tooltip')}
            description={t('window.description')}
            control={
              <SettingsBooleanControl
                checked={isWindowEnabled}
                label={isWindowEnabled ? tCommon('yes') : tCommon('no')}
                disabled={controlsDisabled || !isEnabled}
                onCheckedChange={(details) => {
                  if (!details.checked) {
                    emit({
                      windowEnabled: false,
                      dayOfWeek: null,
                      startHour: null,
                      timezone: null,
                    });

                    return;
                  }

                  emit({
                    windowEnabled: true,
                    dayOfWeek: 1,
                    startHour: 0,
                    timezone: selectedTimezone,
                  });
                }}
              />
            }
          />

          {isWindowEnabled ? (
            <CrawlerWindowDetailsSection
              selectedDay={selectedDay}
              selectedStartHour={selectedStartHour}
              selectedTimezone={selectedTimezone}
              disabled={controlsDisabled}
              onDayChange={(dayOfWeek) => emit({ dayOfWeek })}
              onStartHourChange={(startHour) => emit({ startHour })}
              onTimezoneChange={(timezone) => emit({ timezone })}
            />
          ) : null}
        </VStack>
      </SettingsCard>
    </VStack>
  );
}
